import { Component, forwardRef, inject, input } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import {
  BuildingEquipmentFilterDto,
  BuildingEquipmentItemDto,
  BuildingEquipmentService, BuildingItemDto, BuildingSectionItemDto,
  TenantItemDto,
} from '@artemis-software/wr-api';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  firstValueFrom,
  map,
  switchMap,
} from 'rxjs';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NbDialogService, NbPosition, NbToastrService } from '@nebular/theme';
import { SortableOptions } from 'sortablejs';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '@components/dialog/confirm-dialog/confirm-dialog.component';

@Component({
  standalone: false,
  selector: 'wr-building-equipment-multi-select',
  templateUrl: './building-equipment-multi-select.component.html',
  styleUrls: ['./building-equipment-multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuildingEquipmentMultiSelectComponent),
      multi: true,
    },
  ],
})
export class BuildingEquipmentMultiSelectComponent extends AbstractFormControl<BuildingEquipmentItemDto[]> {

  readonly NbPosition = NbPosition;
  readonly router = inject(Router);
  protected readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);
  protected readonly buildingEquipmentService = inject(BuildingEquipmentService);

  readonly building = input<BuildingItemDto | undefined>();
  readonly creatorTenant = input<TenantItemDto | undefined>();
  readonly buildingSection = input<BuildingSectionItemDto | undefined>();

  buildingEquipments$ = new BehaviorSubject<BuildingEquipmentItemDto[]>([]);
  notSelectedBuildingEquipments$ = combineLatest([this.buildingEquipments$, this.value$]).pipe(
    map(([buildingEquipments, selected]) =>
      buildingEquipments.filter(b => !selected?.find(s => s.id === b.id)),
    ),
  );

  get sortableOptions(): SortableOptions {
    return {
      sort: true,
      disabled: this.isDisabled,
    };
  }

  text$ = new BehaviorSubject<string>('');
  reload$ = new BehaviorSubject<void>(undefined);

  constructor() {
    super();
    combineLatest([
      toObservable(this.building),
      this.text$,
      toObservable(this.creatorTenant),
      this.reload$,
    ]).pipe(
      debounceTime(100),
      filter(([building]) => !!building?.id),
      switchMap(([building, text, creatorTenant]) => this.findAll(building!.id, text, creatorTenant?.id)),
      takeUntilDestroyed(),
    ).subscribe(items => this.buildingEquipments$.next(items));
  }

  private findAll(buildingId: string, text: string, creatorTenantId: string | undefined) {
    const filter: BuildingEquipmentFilterDto = {
      text,
      creatorTenantId,
      buildingId,
    };
    return this.buildingEquipmentService.findAllForBuildingSelection(filter);
  }

  removeBuildingEquipment(be: BuildingEquipmentItemDto) {
    this.value = this.value?.filter(b => b.id !== be.id);
  }

  addBuildingEquipment(buildingEquipment: BuildingEquipmentItemDto): void {
    this.value = [...this.value ?? [], buildingEquipment];
  }

  navigateToBuildingEquipment(be: BuildingEquipmentItemDto) {
    this.router.navigate(['/buildingEquipment', be.id]);
  }

  async deleteBuildingEquipment(be: BuildingEquipmentItemDto): Promise<void> {
    const confirmed = await firstValueFrom(
      this.dialogService.open(ConfirmDialogComponent, {
        context: {
          header: 'TGA löschen',
          message: `Soll "${be.equipment.name}" permanent gelöscht werden? Abgeschlossene Begehungen sind nicht betroffen.`,
          status: 'danger',
          submitText: 'Löschen',
          cancelText: 'Abbrechen',
        },
      }).onClose,
    );
    if (!confirmed) return;

    try {
      await firstValueFrom(this.buildingEquipmentService.merge({ id: be.id, markedForDeletion: true }));
      this.removeBuildingEquipment(be);
      this.toastrService.success('TGA wurde gelöscht.', 'Gelöscht');
    } catch {
      this.toastrService.danger('TGA konnte nicht gelöscht werden.', 'Fehler');
    }
  }
}
